import axios from 'axios'

let apiURI = process.env.REACT_APP_SERVERAPI

// 60000ms: covers ordinary CRUD plus the confirmed Render free-tier cold-start cost, with headroom.
// Arithmetic and sources: src/services/README.md.
const DEFAULT_TIMEOUT_MS = 60000

export const api = axios.create({
    baseURL: apiURI,
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT_MS,
})

const STORAGE_KEY = 'reilm.auth'

const readAccessToken = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw)?.accessToken : null
    } catch {
        return null
    }
}

const writeAccessToken = (accessToken) => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        const prev = raw ? JSON.parse(raw) : {}
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, accessToken }))
    } catch {
        // ignore
    }
}

const clearSession = () => {
    localStorage.removeItem(STORAGE_KEY)
    window.location.hash = '#/login'
}

// Attach Bearer token to internal API requests only
api.interceptors.request.use((config) => {
    if (config.url?.startsWith('https://')) return config;
    const token = readAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auto-refresh: hold parallel 401s until one refresh completes
let refreshPromise = null

const performRefresh = () => {
    if (!refreshPromise) {
        // Bare axios, not the `api` instance -- carries no timeout of its own otherwise, and every
        // parallel 401 waits on this one shared promise, so a hang here strands all of them forever.
        refreshPromise = axios
            .post(`${apiURI}/auth/refresh`, null, { withCredentials: true, timeout: DEFAULT_TIMEOUT_MS })
            .then((res) => {
                const newToken = res.data?.data?.accessToken
                if (!newToken) throw new Error('No accessToken in refresh response')
                writeAccessToken(newToken)
                return newToken
            })
            .finally(() => {
                refreshPromise = null
            })
    }
    return refreshPromise
}

// Calls that must never trigger a refresh-then-retry: retrying /auth/login or /auth/refresh
// itself on their own 401 makes no sense, and /auth/logout's 401 is moot (session's going away
// either way). Everything else authenticated, including /auth/me, gets the same retry as any
// other endpoint -- excluding the whole `/auth/` prefix here previously also caught /auth/me,
// so a single transient 401 revalidating the cached user wiped the session with no recovery
// attempt (Set#5 item 2, 16.08).
const NO_REFRESH_RETRY = ['/auth/login', '/auth/refresh', '/auth/logout']

// On 401: refresh once, then retry the original request. If refresh fails, sign out.
// On 403 with TRIAL_EXPIRED / SUBSCRIPTION_REQUIRED, dispatch event so ExpiredModal renders.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error?.config
        const status = error?.response?.status
        const url = original?.url || ''

        if (status === 401 && original && !original._retry && !NO_REFRESH_RETRY.some((p) => url.includes(p))) {
            original._retry = true
            try {
                const newToken = await performRefresh()
                original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` }
                return api.request(original)
            } catch {
                clearSession()
                return Promise.reject(error)
            }
        }

        if (status === 401) {
            clearSession()
        }

        if (status === 403) {
            const code = error.response.data?.error?.code
            if (code === 'TRIAL_EXPIRED') {
                window.dispatchEvent(new CustomEvent('reilm:subscription-expired', { detail: 'trial' }))
            } else if (code === 'SUBSCRIPTION_REQUIRED') {
                window.dispatchEvent(new CustomEvent('reilm:subscription-expired', { detail: 'subscription' }))
            }
        }

        return Promise.reject(error)
    }
);
